// TEMPORARY DEMO DATA — safe to delete.
//
// Self-contained sample data for the ShareBite "Demo Mode". It does NOT touch
// Firebase or the backend; it only feeds the read-only demo screens so the UI
// can be explored without a configured project. To remove the demo entirely,
// delete the `app/src/demo` folder and the demo wiring in `app/src/App.tsx`.

import type {
  Donation,
  NGOProfile,
  Rating,
  RestaurantProfile,
} from "@sharebite/domain";

const HOUR = 60 * 60 * 1000;
const DAY = 24 * HOUR;
// A fixed "now" so the demo is deterministic regardless of wall-clock time.
export const DEMO_NOW = Date.UTC(2024, 0, 15, 12, 0, 0);

/** Five demo restaurants with varied served categories. */
export const DEMO_RESTAURANTS: RestaurantProfile[] = [
  {
    uid: "demo-r1",
    restaurantName: "Green Spoon Kitchen",
    ownerName: "Aarti Mehta",
    phoneNumber: "5550100101",
    address: "12 Garden Road, Springfield",
    foodCategoriesServed: ["Vegetarian", "Eggetarian"],
    verificationDocRef: "demo/r1.pdf",
  },
  {
    uid: "demo-r2",
    restaurantName: "Coastal Grill House",
    ownerName: "Marco Diaz",
    phoneNumber: "5550100102",
    address: "88 Harbor Ave, Springfield",
    foodCategoriesServed: ["Non-Vegetarian", "Eggetarian"],
    verificationDocRef: "demo/r2.pdf",
  },
  {
    uid: "demo-r3",
    restaurantName: "Sunrise Bakery & Cafe",
    ownerName: "Priya Nair",
    phoneNumber: "5550100103",
    address: "5 Baker Street, Springfield",
    foodCategoriesServed: ["Vegetarian"],
    verificationDocRef: "demo/r3.pdf",
  },
  {
    uid: "demo-r4",
    restaurantName: "Tandoori Junction",
    ownerName: "Sahil Kapoor",
    phoneNumber: "5550100104",
    address: "240 Spice Lane, Springfield",
    foodCategoriesServed: ["Non-Vegetarian", "Vegetarian", "Eggetarian"],
    verificationDocRef: "demo/r4.pdf",
  },
  {
    uid: "demo-r5",
    restaurantName: "Olive & Basil Trattoria",
    ownerName: "Lucia Romano",
    phoneNumber: "5550100105",
    address: "17 Vineyard Court, Springfield",
    foodCategoriesServed: ["Vegetarian", "Eggetarian"],
    verificationDocRef: "demo/r5.pdf",
  },
];

/** Five demo NGO partners with varied accepted categories. */
export const DEMO_NGOS: NGOProfile[] = [
  {
    uid: "demo-n1",
    ngoName: "Helping Hands Foundation",
    representativeName: "Ravi Shankar",
    phoneNumber: "5550200201",
    address: "3 Charity Plaza, Springfield",
    acceptedCategories: ["Vegetarian", "Eggetarian", "Non-Vegetarian"],
    verificationDocRef: "demo/n1.pdf",
  },
  {
    uid: "demo-n2",
    ngoName: "Green Plate Project",
    representativeName: "Sofia Alvarez",
    phoneNumber: "5550200202",
    address: "21 Hope Street, Springfield",
    acceptedCategories: ["Vegetarian"],
    verificationDocRef: "demo/n2.pdf",
  },
  {
    uid: "demo-n3",
    ngoName: "City Shelter Network",
    representativeName: "Daniel Cohen",
    phoneNumber: "5550200203",
    address: "76 Unity Blvd, Springfield",
    acceptedCategories: ["Non-Vegetarian", "Eggetarian"],
    verificationDocRef: "demo/n3.pdf",
  },
  {
    uid: "demo-n4",
    ngoName: "Little Meals Trust",
    representativeName: "Meera Iyer",
    phoneNumber: "5550200204",
    address: "9 Kindness Way, Springfield",
    acceptedCategories: ["Vegetarian", "Eggetarian"],
    verificationDocRef: "demo/n4.pdf",
  },
  {
    uid: "demo-n5",
    ngoName: "Open Door Community Aid",
    representativeName: "Thomas Brown",
    phoneNumber: "5550200205",
    address: "140 Welcome Road, Springfield",
    acceptedCategories: ["Non-Vegetarian", "Vegetarian", "Eggetarian"],
    verificationDocRef: "demo/n5.pdf",
  },
];

function donation(d: Partial<Donation> & Pick<Donation,
  "donationId" | "restaurantUid" | "foodName" | "mealCount" | "foodCategory" | "status">): Donation {
  return {
    description: "",
    imageRefs: ["demo/placeholder.jpg"],
    preparationTime: DEMO_NOW - 2 * HOUR,
    pickupDeadline: DEMO_NOW + 2 * HOUR,
    specialInstructions: "",
    reservedBy: null,
    reservedAt: null,
    createdAt: DEMO_NOW - 3 * HOUR,
    ...d,
  };
}

/** Sample donations across restaurants in a mix of lifecycle statuses. */
export const DEMO_DONATIONS: Donation[] = [
  // Green Spoon Kitchen (r1)
  donation({
    donationId: "demo-d1",
    restaurantUid: "demo-r1",
    foodName: "Vegetable Biryani trays",
    description: "Freshly cooked, never served. Serves a large group.",
    mealCount: 40,
    foodCategory: "Vegetarian",
    status: "Available",
  }),
  donation({
    donationId: "demo-d2",
    restaurantUid: "demo-r1",
    foodName: "Paneer wraps",
    mealCount: 18,
    foodCategory: "Eggetarian",
    status: "Reserved",
    reservedBy: "demo-n4",
    reservedAt: DEMO_NOW - 1 * HOUR,
  }),
  donation({
    donationId: "demo-d3",
    restaurantUid: "demo-r1",
    foodName: "Dal & rice combo",
    mealCount: 30,
    foodCategory: "Vegetarian",
    status: "Completed",
    reservedBy: "demo-n2",
    reservedAt: DEMO_NOW - 2 * DAY,
    createdAt: DEMO_NOW - 2 * DAY - 3 * HOUR,
  }),
  // Coastal Grill House (r2)
  donation({
    donationId: "demo-d4",
    restaurantUid: "demo-r2",
    foodName: "Grilled chicken portions",
    description: "Packed hot, ready for pickup.",
    mealCount: 25,
    foodCategory: "Non-Vegetarian",
    status: "Available",
  }),
  donation({
    donationId: "demo-d5",
    restaurantUid: "demo-r2",
    foodName: "Egg salad bowls",
    mealCount: 20,
    foodCategory: "Eggetarian",
    status: "Completed",
    reservedBy: "demo-n3",
    reservedAt: DEMO_NOW - 3 * DAY,
    createdAt: DEMO_NOW - 3 * DAY - 3 * HOUR,
  }),
  // Sunrise Bakery & Cafe (r3)
  donation({
    donationId: "demo-d6",
    restaurantUid: "demo-r3",
    foodName: "Assorted breads & pastries",
    mealCount: 50,
    foodCategory: "Vegetarian",
    status: "Available",
  }),
  donation({
    donationId: "demo-d7",
    restaurantUid: "demo-r3",
    foodName: "Veg sandwiches",
    mealCount: 22,
    foodCategory: "Vegetarian",
    status: "Completed",
    reservedBy: "demo-n2",
    reservedAt: DEMO_NOW - 1 * DAY,
    createdAt: DEMO_NOW - 1 * DAY - 3 * HOUR,
  }),
  // Tandoori Junction (r4)
  donation({
    donationId: "demo-d8",
    restaurantUid: "demo-r4",
    foodName: "Chicken tandoori platters",
    mealCount: 35,
    foodCategory: "Non-Vegetarian",
    status: "Reserved",
    reservedBy: "demo-n1",
    reservedAt: DEMO_NOW - 30 * 60 * 1000,
  }),
  donation({
    donationId: "demo-d9",
    restaurantUid: "demo-r4",
    foodName: "Mixed veg curry",
    mealCount: 28,
    foodCategory: "Vegetarian",
    status: "Completed",
    reservedBy: "demo-n5",
    reservedAt: DEMO_NOW - 4 * DAY,
    createdAt: DEMO_NOW - 4 * DAY - 3 * HOUR,
  }),
  // Olive & Basil Trattoria (r5)
  donation({
    donationId: "demo-d10",
    restaurantUid: "demo-r5",
    foodName: "Margherita pizzas",
    mealCount: 24,
    foodCategory: "Vegetarian",
    status: "Available",
  }),
  donation({
    donationId: "demo-d11",
    restaurantUid: "demo-r5",
    foodName: "Frittata squares",
    mealCount: 16,
    foodCategory: "Eggetarian",
    status: "Completed",
    reservedBy: "demo-n4",
    reservedAt: DEMO_NOW - 5 * DAY,
    createdAt: DEMO_NOW - 5 * DAY - 3 * HOUR,
  }),
  donation({
    donationId: "demo-d12",
    restaurantUid: "demo-r5",
    foodName: "Pasta primavera",
    mealCount: 12,
    foodCategory: "Vegetarian",
    status: "Expired",
    createdAt: DEMO_NOW - 6 * HOUR,
    pickupDeadline: DEMO_NOW - 1 * HOUR,
  }),
];

/** Sample ratings restaurants gave NGOs for completed donations. */
export const DEMO_RATINGS: Rating[] = [
  { ratingId: "demo-rt1", donationId: "demo-d3", restaurantUid: "demo-r1", ngoUid: "demo-n2", stars: 5, reviewText: "Prompt and friendly pickup.", createdAt: DEMO_NOW - 2 * DAY },
  { ratingId: "demo-rt2", donationId: "demo-d7", restaurantUid: "demo-r3", ngoUid: "demo-n2", stars: 4, reviewText: "Great coordination.", createdAt: DEMO_NOW - 1 * DAY },
  { ratingId: "demo-rt3", donationId: "demo-d5", restaurantUid: "demo-r2", ngoUid: "demo-n3", stars: 5, reviewText: "Very professional.", createdAt: DEMO_NOW - 3 * DAY },
  { ratingId: "demo-rt4", donationId: "demo-d9", restaurantUid: "demo-r4", ngoUid: "demo-n5", stars: 4, reviewText: "On time.", createdAt: DEMO_NOW - 4 * DAY },
  { ratingId: "demo-rt5", donationId: "demo-d11", restaurantUid: "demo-r5", ngoUid: "demo-n4", stars: 5, reviewText: "Lovely volunteers.", createdAt: DEMO_NOW - 5 * DAY },
];

/** Lookup helpers used by the demo screens. */
export function restaurantById(uid: string): RestaurantProfile | undefined {
  return DEMO_RESTAURANTS.find((r) => r.uid === uid);
}
export function ngoById(uid: string): NGOProfile | undefined {
  return DEMO_NGOS.find((n) => n.uid === uid);
}
export function donationsByRestaurant(uid: string): Donation[] {
  return DEMO_DONATIONS.filter((d) => d.restaurantUid === uid);
}
export function ratingsForNGO(uid: string): Rating[] {
  return DEMO_RATINGS.filter((r) => r.ngoUid === uid);
}
